/**
 * Utilitários para cálculo de consumo de produtos de limpeza.
 */

/**
 * Calcula o consumo total de água em litros a partir da lista de tarefas e métricas amostradas.
 *
 * Intenção: Obter o volume de água gasto (real ou projetado) para o dashboard.
 * Exemplo: const water = calculateTotalWaterSpent(list, 1000, { discharge_rate: 15 });
 *
 * @param {Array} list Lista de tarefas.
 * @param {number} totalArea Área total.
 * @param {Object} calculatedMetrics Métricas enviadas pelo backend (discharge_rate amostrada).
 * @param {number} manualDischargeRate Taxa de descarga configurada (ml/m²).
 * @returns {number} Consumo total de água em Litros.
 */
export function calculateTotalWaterSpent(
    list,
    totalArea = 0,
    calculatedMetrics = null,
    manualDischargeRate = 20
) {
    if (!list || list.length === 0) return 0;

    const areaNum = parseFloat(totalArea) || 0;

    // 1. Prioridade: Métrica calculada por amostragem inteligente no backend
    if (calculatedMetrics && calculatedMetrics.discharge_rate) {
        return (areaNum * calculatedMetrics.discharge_rate) / 1000;
    }

    // 2. Secundário: Se houverem dados reais individuais (detalhamento total)
    const hasRealData = list.some(
        item => item.cost_water !== undefined && item.cost_water !== null
    );
    if (hasRealData) {
        const totalMl = list.reduce(
            (acc, item) => acc + (parseFloat(item.cost_water) || 0),
            0
        );
        return totalMl / 1000;
    }

    // 3. Fallback: Estimativa baseada na configuração ou valor padrão
    const rate = parseFloat(manualDischargeRate) || 20;
    return (areaNum * rate) / 1000;
}

/**
 *
 * Intenção: Calcular o volume de produto puro necessário para a mistura.
 * Exemplo: const chem = calculateChemicalConsumption(10, 50); // 500ml
 *
 * @param {number} waterL Volume de água em Litros.
 * @param {number} dilutionRatio Proporção de diluição em ml/L.
 * @returns {number} Volume de produto químico em mililitros.
 */
export function calculateChemicalConsumption(waterL, dilutionRatio) {
    const ratio = parseFloat(dilutionRatio) || 10;
    return waterL * ratio;
}

/**
 *
 * Intenção: Comparar a eficiência do robô contra o método de limpeza tradicional.
 * Exemplo: const saved = calculateWaterSavings(100, 2); // Litros economizados
 *
 * @param {number} totalArea Área total limpa.
 * @param {number} waterL Água realmente gasta pelo robô.
 * @param {number} manualReference Referência manual em ml/m².
 * @returns {number} Economia em Litros.
 */
export function calculateWaterSavings(
    totalArea,
    waterL,
    manualReference = 500
) {
    const areaNum = parseFloat(totalArea) || 0;
    const manualRefL = (areaNum * manualReference) / 1000;
    const savings = manualRefL - waterL;
    return Math.max(0, savings);
}

/**
 * Formata volumes de fluidos para exibição amigável (ml vs L).
 *
 * Intenção: Converter automaticamente mililitros para litros quando o volume
 * for significativo, melhorando a leitura para o cliente.
 *
 * @param {number} ml Volume em mililitros.
 * @returns {Object} Objeto com valor formatado e unidade.
 */
export function formatFluidVolume(ml) {
    const value = parseFloat(ml) || 0;
    if (value >= 1000) {
        return {
            value: (value / 1000).toFixed(1),
            unit: 'L',
        };
    }
    return {
        value: value.toFixed(0),
        unit: 'ml',
    };
}

/**
 * Calcula a média de diluição entre os robôs da lista.
 *
 * Intenção: Obter uma taxa de diluição representativa para o dashboard quando
 * múltiplos robôs com configurações diferentes estão sendo visualizados.
 *
 * @param {Array} list Lista de tarefas.
 * @param {Object} applications Mapeamento de configurações por robô.
 * @param {number} defaultRatio Diluição padrão se não houver dados.
 * @returns {number} Média de diluição em ml/L.
 */
export function calculateAverageDilution(
    list,
    applications,
    defaultRatio = 10
) {
    if (!list || list.length === 0 || !applications) return defaultRatio;

    const robotIds = Array.from(
        new Set(list.map(t => t._robot && t._robot.id).filter(Boolean))
    );
    if (robotIds.length === 0) return defaultRatio;

    let total = 0;
    let count = 0;

    robotIds.forEach(id => {
        if (applications[id] && applications[id].chemicalDilutionRatio) {
            total += parseFloat(applications[id].chemicalDilutionRatio);
            count++;
        }
    });

    return count > 0 ? total / count : defaultRatio;
}
